import { Button } from "@/components/ui/button";
import { Course } from "@/types/course";
import {
  CircleDashed,
  ExternalLink,
} from "lucide-react";
import { useState } from "react";
import { CourseHeader } from "../CourseHeader";
import { StudentPortalAssessmentCard } from "./StudentPortalAssessmentCard";
import {
  StudentPortalAssessment,
  StudentPortalLane,
} from "./types";
import { Badge } from "@/components/ui/badge";

interface StudentPortalAssessmentsProps {
  course: Course;
  studentCount: number;
  shortUrl?: string;
  laneAssessments: Record<StudentPortalLane, StudentPortalAssessment[]>;
  canPublish?: boolean;
  onUploadAssessment: () => void;
  onMoveAssessment: (assessmentId: string, lane: StudentPortalLane) => void;
  onEdit: (assessment: StudentPortalAssessment) => void;
  onToggleGrades: (assessmentId: string, feedbackLive: boolean) => void;
  onShare: () => void;
}

export const StudentPortalAssessments = ({
  course,
  studentCount,
  shortUrl,
  laneAssessments,
  canPublish = true,
  onUploadAssessment,
  onMoveAssessment,
  onEdit,
  onToggleGrades,
  onShare,
}: StudentPortalAssessmentsProps) => {
  const [draggingAssessmentId, setDraggingAssessmentId] = useState<string | null>(null);
  const [dragOverLane, setDragOverLane] = useState<StudentPortalLane | null>(null);

  const lanes: { key: StudentPortalLane; label: string }[] = [
    { key: "scheduled", label: "Scheduled" },
    { key: "ongoing", label: "Live in portal" },
    { key: "review", label: "In review" },
    { key: "completed", label: "Completed" },
  ];

  const laneColors: Record<StudentPortalLane, string> = {
    scheduled: "bg-muted/20",
    ongoing: "bg-[#FAFCFE]",
    review: "bg-[#FFFBF9]",
    completed: "bg-[#FBFEFA]",
  };

  const laneDropColors: Record<StudentPortalLane, string> = {
    scheduled: "bg-muted/40",
    ongoing: "bg-[#F5F9FE]",
    review: "bg-[#FFF7F4]",
    completed: "bg-[#F6FBF4]",
  };

  const draggedAssessment = draggingAssessmentId
    ? Object.values(laneAssessments).flat().find((a) => a.id === draggingAssessmentId)
    : null;

  const draggingLane = draggingAssessmentId
    ? lanes.find((lane) =>
        laneAssessments[lane.key].some(
          (assessment) => assessment.id === draggingAssessmentId
        )
      )?.key
    : null;

  return (
    <div className="pb-8">
      <CourseHeader
        title="Student Portal"
        courseTitle={course.title}
        className="max-w-7xl mx-auto"
        info={
          <>
            <div>
              <span className="text-muted-foreground">Students enrolled: </span>
              <Badge variant="secondary">
                {studentCount}
              </Badge>
            </div>
            {shortUrl && (
              <Button variant="link" size="sm" className="h-auto p-0 text-muted-foreground hover:text-foreground" asChild>
                <a
                  href={shortUrl}
                  target="_blank"
                  rel="noreferrer"
                  title="Open student portal"
                >
                  <span className="flex items-center gap-1.5">
                    Open portal
                    <ExternalLink className="h-3 w-3" />
                  </span>
                </a>
              </Button>
            )}
          </>
        }
        actions={
          <>
            <Button size="sm" onClick={onUploadAssessment} disabled={!canPublish}>
              Publish assessment
            </Button>
            <Button variant="outline" size="sm" onClick={onShare}>
              Share course
            </Button>
          </>
        }
      />

      <div className="max-w-7xl mx-auto">
      <div className="flex w-full gap-4 overflow-x-auto pb-2">
        
        {lanes.map((lane) => {
          const assessments = laneAssessments[lane.key];
          const isOtherLane = !!draggingLane && draggingLane !== lane.key;
          const isReviewRestricted = lane.key === "review" && draggedAssessment?.isFormative;
          const canDrop = isOtherLane && !isReviewRestricted;
          const isDropTarget = dragOverLane === lane.key && canDrop;
          return (
            <div
              key={lane.key}
              data-testid={`student-portal-lane-${lane.key}`}
              className={`rounded-lg p-3 min-h-[70vh] transition-all shrink-0 min-w-[280px] max-w-[320px] ${
                isDropTarget
                  ? laneDropColors[lane.key]
                  : laneColors[lane.key]
              }`}
              onDragOver={(event) => {
                event.preventDefault();
                event.dataTransfer.dropEffect = canDrop ? "move" : "none";
                if (canDrop) {
                  setDragOverLane(lane.key);
                }
              }}
              onDragLeave={() => {
                setDragOverLane((current) => (current === lane.key ? null : current));
              }}
              onDrop={(event) => {
                event.preventDefault();
                if (canDrop) {
                  const assessmentId = event.dataTransfer.getData("assessment-id");
                  if (assessmentId && draggingLane !== lane.key) {
                    onMoveAssessment(assessmentId, lane.key);
                  }
                }
                setDraggingAssessmentId(null);
                setDragOverLane(null);
              }}
            >
              <div className="mb-3 flex items-center justify-between">
                <h4 className="text-sm font-normal text-muted-foreground">{lane.label}</h4>
                <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-medium">
                  {assessments.length}
                </span>
              </div>
 
              <div className="space-y-3">
                {assessments.map((assessment) => (
                  <StudentPortalAssessmentCard
                    key={assessment.id}
                    assessment={assessment}
                    lane={lane.key}
                    isDragging={draggingAssessmentId === assessment.id}
                    onDragStart={(assessmentId, event) => {
                      event.dataTransfer.setData("assessment-id", assessmentId);
                      event.dataTransfer.effectAllowed = "move";
                      setDraggingAssessmentId(assessmentId);
                    }}
                    onDragEnd={() => {
                      setDraggingAssessmentId(null);
                      setDragOverLane(null);
                    }}
                    onEdit={onEdit}
                    onToggleGrades={onToggleGrades}
                  />
                ))}

                {assessments.length === 0 && (
                  <div className="rounded-lg bg-muted/10 p-6 text-center text-sm text-muted-foreground">
                    <CircleDashed className="mx-auto mb-2 h-5 w-5" />
                    No assessments
                  </div>
                )}
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
};
